export type JsonataCompleterItem = {
  label: string; // The label to display in the suggestion
  type: string; // The type of the suggestion (e.g., function, operator)
  category: string; // The category of the suggestion (e.g., array, object)
  detail: string; // Additional details about the suggestion
  info: string; // Information about how to use the suggestion
  example: string; // Example usage of the suggestion
};

export const JsonataSuggestions: JsonataCompleterItem[] = [
  {
    label: '$sum',
    type: 'function',
    category: 'numeric',
    detail: 'Aggregates values into a sum',
    info: 'Use this to get the total sum of numeric values in an array or sequence.',
    example: '$sum([1,2,3,4]) // Returns 10'
  },
  {
    label: '$average',
    type: 'function',
    category: 'numeric',
    detail: 'Calculates the average',
    info: 'Returns the mean value of all numeric elements in a sequence.',
    example: '$average([1,2,3,4]) // Returns 2.5'
  },
  {
    label: '$min',
    type: 'function',
    category: 'numeric',
    detail: 'Finds minimum value',
    info: 'Returns the minimum value from an array of numbers.',
    example: '$min([5,2,8,1]) // Returns 1'
  },
  {
    label: '$max',
    type: 'function',
    category: 'numeric',
    detail: 'Finds maximum value',
    info: 'Returns the maximum value from an array of numbers.',
    example: '$max([5,2,8,1]) // Returns 8'
  },
  {
    label: '$length',
    type: 'function',
    category: 'string',
    detail: 'Finds the length of a sequence or string',
    info: 'Determine the number of items in an array or the number of characters in a string.',
    example: '$length("hello") // Returns 5'
  },
  {
    label: 'true',
    type: 'boolean',
    category: 'boolean',
    detail: 'The boolean true value',
    info: 'Represents a boolean value that evaluates to true.',
    example: 'price > 100 ? true : false'
  },
  {
    label: 'false',
    type: 'boolean',
    category: 'boolean',
    detail: 'The boolean false value',
    info: 'Represents a boolean value that evaluates to false.',
    example: 'available = false'
  },
  {
    label: '$not',
    type: 'function',
    category: 'boolean',
    detail: 'Logical NOT operation',
    info: 'Negates a boolean value. Use to invert true/false results.',
    example: '$not(price < 10) // Returns true if price >= 10'
  },
  {
    label: '$exists',
    type: 'function',
    category: 'object',
    detail: 'Checks if a value exists',
    info: 'Returns true if the argument has a value, otherwise false.',
    example: '$exists(address.zipcode) // Returns true if zipcode exists'
  },
  {
    label: '$keys',
    type: 'function',
    category: 'object',
    detail: 'Returns the keys of an object',
    info: 'Generates an array of all keys from the specified object.',
    example: '$keys({"a":1,"b":2}) // Returns ["a","b"]'
  },
  {
    label: '$merge',
    type: 'function',
    category: 'object',
    detail: 'Merges multiple objects',
    info: 'Takes an array of objects and merges them into a single object. In case of duplicate keys, the last one wins.',
    example: '$merge([{"a":1},{"b":2},{"a":3}]) // Returns {"a":3,"b":2}'
  },

  {
    label: '$each',
    type: 'function',
    category: 'object',
    detail: 'Iterates over object properties',
    info: 'Applies a function to each key/value pair in an object and returns an array of results.',
    example: '$each({"a":1,"b":2}, function($k,$v){$k & ":" & $v}) // Returns ["a:1","b:2"]'
  },

  {
    label: '$error',
    type: 'function',
    category: 'object',
    detail: 'Throws an error',
    info: 'Explicitly throws an error with an optional message and error code.',
    example: '$error("Invalid input", "ERR001")'
  },

  {
    label: '$assert',
    type: 'function',
    category: 'object',
    detail: 'Asserts a condition',
    info: 'Tests if a condition is true and throws an error if false.',
    example: '$assert(price > 0, "Price must be positive")'
  },

  {
    label: '$type',
    type: 'function',
    category: 'object',
    detail: 'Returns the type of a value',
    info: 'Returns a string representing the type of the given value (string, number, boolean, null, array, object, function).',
    example: '$type(42) // Returns "number"'
  },
  {
    label: '$lookup',
    type: 'function',
    category: 'object',
    detail: 'Performs a lookup in an object',
    info: 'Retrieve the value of an object property by its key.',
    example: '$lookup({"a":1,"b":2}, "a") // Returns 1'
  },
  {
    label: '$and',
    type: 'operator',
    category: 'boolean',
    detail: 'Logical AND operator',
    info: 'Evaluates to true if both operands are true.',
    example: 'price > 10 and price < 20 // Range check'
  },
  {
    label: '$or',
    type: 'operator',
    category: 'boolean',
    detail: 'Logical OR operator',
    info: 'Evaluates to true if either operand is true.',
    example: 'status = "active" or status = "processing" // Returns true if either matches'
  },
  {
    label: '$in',
    type: 'operator',
    category: 'array',
    detail: 'Checks for membership',
    info: 'Tests whether a value is present in an array or sequence.',
    example: 'color $in ["red","green","blue"]'
  },
  {
    label: '$string',
    type: 'function',
    category: 'string',
    detail: 'Converts value to string',
    info: 'Converts the argument to a string representation.',
    example: '$string(42) // Returns "42"'
  },
  {
    label: '$substring',
    type: 'function',
    category: 'string',
    detail: 'Extracts part of a string',
    info: 'Returns a portion of a string between specified indices.',
    example: '$substring("Hello World", 0, 5) // Returns "Hello"'
  },
  {
    label: '$substringBefore',
    type: 'function',
    category: 'string',
    detail: 'Gets substring before delimiter',
    info: 'Returns the substring that occurs before a specified delimiter.',
    example: '$substringBefore("Hello World", " ") // Returns "Hello"'
  },
  {
    label: '$substringAfter',
    type: 'function',
    category: 'string',
    detail: 'Gets substring after delimiter',
    info: 'Returns the substring that occurs after a specified delimiter.',
    example: '$substringAfter("Hello World", " ") // Returns "World"'
  },
  {
    label: '$uppercase',
    type: 'function',
    category: 'string',
    detail: 'Converts to uppercase',
    info: 'Converts all characters in a string to uppercase.',
    example: '$uppercase("Hello") // Returns "HELLO"'
  },
  {
    label: '$lowercase',
    type: 'function',
    category: 'string',
    detail: 'Converts to lowercase',
    info: 'Converts all characters in a string to lowercase.',
    example: '$lowercase("Hello") // Returns "hello"'
  },
  {
    label: '$trim',
    type: 'function',
    category: 'string',
    detail: 'Removes whitespace',
    info: 'Removes leading and trailing whitespace from a string.',
    example: '$trim("  Hello  ") // Returns "Hello"'
  },
  {
    label: '$pad',
    type: 'function',
    category: 'string',
    detail: 'Pads a string',
    info: 'Pads a string to a specified length with optional padding characters.',
    example: '$pad("42", 5, "0") // Returns "00042"'
  },
  {
    label: '$contains',
    type: 'function',
    category: 'string',
    detail: 'Checks if string contains substring',
    info: 'Tests if a string contains a specified substring.',
    example: '$contains("Hello World", "World") // Returns true'
  },
  {
    label: '$split',
    type: 'function',
    category: 'string',
    detail: 'Splits string into array',
    info: 'Splits a string into an array of substrings based on a delimiter.',
    example: '$split("a,b,c", ",") // Returns ["a","b","c"]'
  },
  {
    label: '$join',
    type: 'function',
    category: 'array',
    detail: 'Joins array into string',
    info: 'Combines an array of strings into a single string with a specified separator.',
    example: '$join(["a","b","c"], "-") // Returns "a-b-c"'
  },
  {
    label: '$match',
    type: 'function',
    category: 'string',
    detail: 'Pattern matching on strings',
    info: 'Matches a string against a regular expression pattern.',
    example: '$match("abc123", /\\d+/) // Returns ["123"]'
  },
  {
    label: '$replace',
    type: 'function',
    category: 'string',
    detail: 'Replaces occurrences in string',
    info: 'Replaces substrings that match a pattern with a replacement string.',
    example: '$replace("Hello World", "World", "JSONata") // Returns "Hello JSONata"'
  },
  {
    label: '$eval',
    type: 'function',
    category: 'other',
    detail: 'Evaluates JSONata expression',
    info: 'Parses and evaluates a JSONata expression provided as a string.',
    example: '$eval("a.b", {"a":{"b":42}}) // Returns 42'
  },
  {
    label: '$base64encode',
    type: 'function',
    category: 'string',
    detail: 'Encodes to base64',
    info: 'Converts a string to a base64 encoded string.',
    example: '$base64encode("Hello") // Returns "SGVsbG8="'
  },
  {
    label: '$base64decode',
    type: 'function',
    category: 'string',
    detail: 'Decodes from base64',
    info: 'Converts a base64 encoded string back to a string.',
    example: '$base64decode("SGVsbG8=") // Returns "Hello"'
  },
  {
    label: '$encodeUrlComponent',
    type: 'function',
    category: 'string',
    detail: 'URL encodes a component',
    info: 'Encodes a URL component by escaping special characters.',
    example: '$encodeUrlComponent("a b") // Returns "a%20b"'
  },
  {
    label: '$encodeUrl',
    type: 'function',
    category: 'string',
    detail: 'URL encodes a string',
    info: 'Encodes an entire URL by escaping special characters.',
    example: '$encodeUrl("https://example.com/a b") // Returns "https://example.com/a%20b"'
  },
  {
    label: '$decodeUrlComponent',
    type: 'function',
    category: 'string',
    detail: 'Decodes URL component',
    info: 'Decodes a URL component by unescaping special characters.',
    example: '$decodeUrlComponent("a%20b") // Returns "a b"'
  },
  {
    label: '$decodeUrl',
    type: 'function',
    category: 'string',
    detail: 'Decodes URL string',
    info: 'Decodes an entire URL by unescaping special characters.',
    example: '$decodeUrl("https://example.com/a%20b") // Returns "https://example.com/a b"'
  },
  {
    label: '$number',
    type: 'function',
    category: 'numeric',
    detail: 'Converts to number',
    info: 'Converts the argument to a number representation.',
    example: '$number("42") // Returns 42'
  },
  {
    label: '$abs',
    type: 'function',
    category: 'numeric',
    detail: 'Returns absolute value',
    info: 'Returns the absolute value of a number.',
    example: '$abs(-42) // Returns 42'
  },
  {
    label: '$floor',
    type: 'function',
    category: 'numeric',
    detail: 'Rounds down to integer',
    info: 'Returns the largest integer less than or equal to the argument.',
    example: '$floor(3.7) // Returns 3'
  },
  {
    label: '$ceil',
    type: 'function',
    category: 'numeric',
    detail: 'Rounds up to integer',
    info: 'Returns the smallest integer greater than or equal to the argument.',
    example: '$ceil(3.1) // Returns 4'
  },
  {
    label: '$round',
    type: 'function',
    category: 'numeric',
    detail: 'Rounds to nearest integer',
    info: 'Returns the value rounded to the nearest integer.',
    example: '$round(3.5) // Returns 4'
  },
  {
    label: '$power',
    type: 'function',
    category: 'numeric',
    detail: 'Calculates exponentiation',
    info: 'Returns the first argument raised to the power of the second argument.',
    example: '$power(2, 3) // Returns 8'
  },
  {
    label: '$sqrt',
    type: 'function',
    category: 'numeric',
    detail: 'Calculates square root',
    info: 'Returns the square root of the argument.',
    example: '$sqrt(16) // Returns 4'
  },
  {
    label: '$random',
    type: 'function',
    category: 'numeric',
    detail: 'Generates random number',
    info: 'Returns a pseudo-random number between 0 (inclusive) and 1 (exclusive).',
    example: '$random() // Returns a value like 0.7564…'
  },
  {
    label: '$formatNumber',
    type: 'function',
    category: 'numeric',
    detail: 'Formats number as string',
    info: 'Converts a number to a string using locale-specific formatting.',
    example: '$formatNumber(1234.5678, "#,##0.00") // Returns "1,234.57"'
  },
  {
    label: '$formatBase',
    type: 'function',
    category: 'numeric',
    detail: 'Formats to different base',
    info: 'Converts a number to a string in a specified base (radix).',
    example: '$formatBase(42, 16) // Returns "2a" (hex)'
  },
  {
    label: '$formatInteger',
    type: 'function',
    category: 'numeric',
    detail: 'Formats integer with grouping',
    info: 'Formats an integer using locale-specific grouping separators.',
    example: '$formatInteger(1234567, "en") // Returns "1,234,567"'
  },
  {
    label: '$parseInteger',
    type: 'function',
    category: 'numeric',
    detail: 'Parses string to integer',
    info: 'Parses a string and returns an integer value in the specified radix.',
    example: '$parseInteger("2a", 16) // Returns 42'
  },
  {
    label: '$append',
    type: 'function',
    category: 'array',
    detail: 'Combines arrays',
    info: 'Appends two arrays together to create a new array.',
    example: '$append([1,2], [3,4]) // Returns [1,2,3,4]'
  },
  {
    label: '$count',
    type: 'function',
    category: 'array',
    detail: 'Counts array items',
    info: 'Returns the number of items in the array parameter.',
    example: '$count([1,2,3]) // Returns 3'
  },
  {
    label: '$distinct',
    type: 'function',
    category: 'array',
    detail: 'Removes duplicates',
    info: 'Returns an array with duplicate values removed.',
    example: '$distinct([1,2,2,3,1]) // Returns [1,2,3]'
  },
  {
    label: '$reverse',
    type: 'function',
    category: 'array',
    detail: 'Reverses array order',
    info: 'Returns a new array containing the items in reverse order.',
    example: '$reverse([1,2,3]) // Returns [3,2,1]'
  },
  {
    label: '$shuffle',
    type: 'function',
    category: 'array',
    detail: 'Randomizes array order',
    info: 'Returns a new array containing the items in a random order.',
    example: '$shuffle([1,2,3,4]) // Returns something like [3,1,4,2]'
  },
  {
    label: '$sort',
    type: 'function',
    category: 'array',
    detail: 'Sorts array items',
    info: 'Returns a new array containing the items sorted according to criteria.',
    example: '$sort([3,1,4,2]) // Returns [1,2,3,4]'
  },
  {
    label: '$zip',
    type: 'function',
    category: 'array',
    detail: 'Combines multiple arrays',
    info: 'Returns a new array by combining multiple arrays based on their position.',
    example: '$zip([1,2], ["a","b"]) // Returns [[1,"a"], [2,"b"]]'
  },
  {
    label: '$map',
    type: 'function',
    category: 'array',
    detail: 'Transforms array items',
    info: 'Applies a function to each item in an array and returns new array with results.',
    example: '$map([1,2,3], function($v) { $v * 2 }) // Returns [2,4,6]'
  },
  {
    label: '$filter',
    type: 'function',
    category: 'array',
    detail: 'Filters array items',
    info: 'Returns only the items in an array that satisfy a specified condition.',
    example: '$filter([1,2,3,4], function($v) { $v > 2 }) // Returns [3,4]'
  },
  {
    label: '$reduce',
    type: 'function',
    category: 'array',
    detail: 'Accumulates array items',
    info: 'Processes an array to produce a single value by applying a function to each item.',
    example: '$reduce([1,2,3], function($a, $v) { $a + $v }, 0) // Returns 6'
  },
  {
    label: '$sift',
    type: 'function',
    category: 'object',
    detail: 'Filters object properties',
    info: "Filters an object's properties based on their values.",
    example: '$sift({"a":1,"b":0,"c":3}, function($v) { $v }) // Returns {"a":1,"c":3}'
  },
  {
    label: '=',
    type: 'operator',
    category: 'boolean',
    detail: 'Equal to',
    info: 'Compares two values for equality.',
    example: 'price = 100 // Returns true if price equals 100'
  },
  {
    label: '!=',
    type: 'operator',
    category: 'boolean',
    detail: 'Not equal to',
    info: 'Compares two values for inequality.',
    example: 'status != "completed" // Returns true if not completed'
  },
  {
    label: '<',
    type: 'operator',
    category: 'boolean',
    detail: 'Less than',
    info: 'Checks if the first value is less than the second value.',
    example: 'price < 50 // Returns true if price is less than 50'
  },
  {
    label: '<=',
    type: 'operator',
    category: 'boolean',
    detail: 'Less than or equal to',
    info: 'Checks if the first value is less than or equal to the second value.',
    example: 'qty <= 10 // Returns true if qty is 10 or less'
  },
  {
    label: '>',
    type: 'operator',
    category: 'boolean',
    detail: 'Greater than',
    info: 'Checks if the first value is greater than the second value.',
    example: 'age > 18 // Returns true if age is greater than 18'
  },
  {
    label: '>=',
    type: 'operator',
    category: 'boolean',
    detail: 'Greater than or equal to',
    info: 'Checks if the first value is greater than or equal to the second value.',
    example: 'rating >= 4 // Returns true if rating is 4 or higher'
  },
  {
    label: 'in',
    type: 'operator',
    category: 'array',
    detail: 'Membership test',
    info: 'Tests if a value is contained within an array.',
    example: '"red" in ["red", "green", "blue"] // Returns true'
  },
  {
    label: 'and',
    type: 'operator',
    category: 'boolean',
    detail: 'Logical AND',
    info: 'Combines two Boolean expressions, evaluates to true only if both expressions are true.',
    example: 'price > 10 and price < 20 // Returns true for values between'
  },
  {
    label: 'or',
    type: 'operator',
    category: 'boolean',
    detail: 'Logical OR',
    info: 'Combines two Boolean expressions, evaluates to true if either expression is true.',
    example: 'status = "new" or status = "processing" // Returns true if either matches'
  },
  {
    label: '.',
    type: 'operator',
    category: 'object',
    detail: 'Map operator',
    info: 'Selects and processes each property from an object or item in an array.',
    example: 'products.price // Returns array of prices from products array'
  },
  {
    label: '[ ]',
    type: 'operator',
    category: 'array',
    detail: 'Filter operator',
    info: 'Filters a sequence by criteria, or accesses array elements by index.',
    example: 'products[price > 100] // Returns products with price > 100'
  },
  {
    label: '^( )',
    type: 'operator',
    category: 'array',
    detail: 'Order-by operator',
    info: 'Sorts the input sequence according to the ordering expressions.',
    example: 'products^(price) // Sorts products by price ascending'
  },
  {
    label: '{ }',
    type: 'operator',
    category: 'object',
    detail: 'Reduce operator',
    info: 'Groups and reduces input sequence data into a new structure.',
    example: 'products{category: {name: name, items: $}} // Groups by category'
  },
  {
    label: '*',
    type: 'operator',
    category: 'object',
    detail: 'Wildcard operator',
    info: 'Selects all properties of an object or all items in an array.',
    example: 'address.* // Returns all properties of the address object'
  },
  {
    label: '**',
    type: 'operator',
    category: 'object',
    detail: 'Descendants operator',
    info: 'Recursively selects all descendants (nested properties) of the input.',
    example: 'data**.price // Finds all prices at any level in the data'
  },
  {
    label: '%',
    type: 'operator',
    category: 'other',
    detail: 'Parent operator',
    info: 'References the parent of the current context in the input path.',
    example: 'items[%.type="box"] // Filters using parent context'
  },
  {
    label: '#',
    type: 'operator',
    category: 'array',
    detail: 'Positional variable',
    info: 'Binds the position (index) of the current item in a sequence.',
    example: 'items#$i[$i > 2] // Returns items at position > 2'
  },
  {
    label: '@',
    type: 'operator',
    category: 'other',
    detail: 'Context variable',
    info: 'Binds the current context item for reference within predicates or expressions.',
    example: 'items[@.price > 100] // References current item in filter'
  },
  {
    label: '+',
    type: 'operator',
    category: 'numeric',
    detail: 'Addition',
    info: 'Adds two numeric values together.',
    example: 'price + tax // Returns sum of price and tax'
  },
  {
    label: '-',
    type: 'operator',
    category: 'numeric',
    detail: 'Subtraction/Negation',
    info: 'Subtracts second value from first, or negates a single value.',
    example: 'total - discount // Returns difference'
  },
  {
    label: '*',
    type: 'operator',
    category: 'numeric',
    detail: 'Multiplication',
    info: 'Multiplies two numeric values together.',
    example: 'price * quantity // Returns product of price and quantity'
  },
  {
    label: '/',
    type: 'operator',
    category: 'numeric',
    detail: 'Division',
    info: 'Divides the first value by the second value.',
    example: 'total / count // Returns quotient'
  },
  {
    label: '%',
    type: 'operator',
    category: 'numeric',
    detail: 'Modulo',
    info: 'Returns the remainder after division of the first value by the second.',
    example: 'amount % 2 // Returns 0 for even numbers, 1 for odd'
  },
  {
    label: '..',
    type: 'operator',
    category: 'array',
    detail: 'Range',
    info: 'Creates a sequence of integers from the first value to the second value.',
    example: '1..5 // Returns [1,2,3,4,5]'
  },
  {
    label: '&',
    type: 'operator',
    category: 'string',
    detail: 'Concatenation',
    info: 'Joins two strings together into a single string.',
    example: 'firstName & " " & lastName // Returns full name'
  },
  {
    label: '?:',
    type: 'operator',
    category: 'other',
    detail: 'Conditional',
    info: 'Tests a condition and returns one of two expressions based on the result.',
    example: 'price > 100 ? "expensive" : "affordable"'
  },
  {
    label: ':=',
    type: 'operator',
    category: 'other',
    detail: 'Variable binding',
    info: 'Assigns a value to a variable for use within the current scope.',
    example: '$tax := 0.07; price * (1 + $tax)'
  },
  {
    label: '~>',
    type: 'operator',
    category: 'other',
    detail: 'Chain',
    info: 'Passes the result of an expression as input to a subsequent expression.',
    example: 'data ~> $sort(price) ~> $filter(function($v) { $v.available })'
  },
  {
    label: '... ~> | ... | ...',
    type: 'operator',
    category: 'other',
    detail: 'Transform',
    info: 'Creates a sequence of processing stages where data flows through each step.',
    example: 'data ~> | $sort(price) | $filter(function($v) { $v.available }) |'
  }
];

export const jsonataCompleter = (context: {
  matchBefore: (arg0: RegExp) => { (): unknown; new (): unknown; from: number };
  pos: number;
}) => {
  return {
    from: context.matchBefore(/\w*/)?.from || context.pos, // Start of suggestion range
    options: JsonataSuggestions // Return the completions
  };
};
